import {AfterViewChecked, ChangeDetectorRef, Component, Input} from '@angular/core';
import {FormGroup, ReactiveFormsModule} from "@angular/forms";
import {NgIf} from "@angular/common";
import {SpinnerComponent} from "@shared/components/spinner/spinner.component";
import {FormsService} from "@shared/api/services/v2/forms.service";
import {Form, FormQuestion, FormResult} from "@shared/api/models/query/form.model";
import {FormFieldComponent} from "@shared/components/questionnaire-form/form-field.component";
import {isEmptyError} from "@shared/utils/empty-error";
import {FormQuestionsService} from "@shared/api/services/v2/form-questions.service";

@Component({
    selector: 'questionnaire-form',
    imports: [
        ReactiveFormsModule,
        NgIf,
        FormFieldComponent,
        SpinnerComponent
    ],
    standalone: true,
    templateUrl: './questionnaire-form.component.html'
})
export class QuestionnaireFormComponent implements AfterViewChecked {
    @Input() disabled: boolean = false

    form: Form
    questions: FormQuestion[]
    initialValues: FormResult[]
    type: string

    loading: boolean = true
    loadFailed: boolean = false
    formGroup = new FormGroup({})

    index: number = 1

    constructor(
        private formsService: FormsService,
        private formQuestionsService: FormQuestionsService,
        private changeRef: ChangeDetectorRef
    ) {
    }

    ngAfterViewChecked(): void {
        if (this.disabled) {
            this.formGroup.disable()
            this.changeRef.detectChanges()
        }
    }

    init(form: Form, initialValues?: FormResult[]) {
        this.loading = true
        this.loadFailed = false
        this.questions = []
        this.formGroup = new FormGroup({})
        this.form = form
        this.initialValues = initialValues
        this.formQuestionsService.setParent(form.id)
        this.queryFormQuestions()
    }

    setInitialValues() {
        if (this.initialValues != null) {
            this.initialValues.forEach(formResult => {
                const question = this.searchQuestion(this.questions, formResult.id)
                if (question != null) {
                    question.answer = formResult.answer
                }
            })
        }
    }

    isValid(): boolean {
        // Still loading or failed to load: no questions yet, an empty form is "valid" but must never be submitted.
        if (this.loading || this.loadFailed) {
            return false
        }
        const valid = this.formGroup.valid
        if (!valid) {
            this.formGroup.markAllAsTouched()
        }
        return valid
    }

    disable(): void {
        this.disabled = true
    }

    getResult() {
        let result = []
        this.questions.forEach((question: FormQuestion) => {
            result.push(this.getQuestionResult(question))
        })
        return result
    }

    private getQuestionResult(question: FormQuestion) {
        let answer = null
        const value = this.formGroup.get("question" + question.id).value
        if (value == undefined) {
            return undefined
        }
        if (question.type == "checkbox") {
            answer = []
            question.options.forEach((option) => {
                if (value["option" + option.id]) {
                    answer.push(option.id)
                }
            })
        } else {
            answer = value.toString()
        }
        return {
            id: question.id,
            answer: answer,
        }
    }

    private async queryFormQuestions() {
        let response
        try {
            response = await this.formQuestionsService.listAllStrict()
        } catch (error) {
            // Show a failed load, never an empty form.
            this.questions = []
            this.loadFailed = true
            this.loading = false
            // EmptyError: the interceptor already told the user. Anything else (status 0, 400, 422, 502...)
            // was rethrown by the interceptor and must still reach the global error handler / Sentry.
            if (!isEmptyError(error)) {
                throw error
            }
            return
        }
        let index = 1
        response.data.forEach(question => {
            if (question.conditions) {
                question.index = ""
            } else {
                question.index = (index++).toString()
            }
        })
        this.questions = response.data
        setTimeout(() => {
            this.setInitialValues()
        }, 100)

        this.loading = false
    }

    private searchQuestion(questions: FormQuestion[], id: number): FormQuestion | null {
        let selectedQuestion = null
        questions.forEach(question => {
            if (question.id == id) {
                selectedQuestion = question
            }
        })
        return selectedQuestion
    }
}
